// Canonical end-to-end journey of an ethics-review dossier.
// Reused by the "Hoe werkt het?" explainer (full narrative) and the
// "you are here" strip on the dossier detail page (compact variant).

export type ProcessStageId =
  | "intake"
  | "prescreening"
  | "klaar-check"
  | "bureau"
  | "advies"
  | "afgerond";

export type StageActor = "human" | "ai" | "system";

export interface ProcessStage {
  id: ProcessStageId;
  /** Short label shown under the stepper node — matches app vocabulary. */
  label: string;
  /** Wie voert deze stap uit. */
  actor: StageActor;
  /** Korte samenvatting van wat er gebeurt (voor de compacte weergave). */
  summary: string;
  /** Uitgebreide uitleg voor de narratieve explainer. */
  detail: string;
  /** Eenregelige "wat gebeurt hierna" hint wanneer een dossier in deze fase zit. */
  next: string;
}

export const PROCESS_STAGES: ProcessStage[] = [
  {
    id: "intake",
    label: "Intake",
    actor: "human",
    summary: "Begeleide indiening",
    detail:
      "De onderzoeker dient de aanvraag stap voor stap in via het begeleide formulier. Etna helpt om elk luik correct en volledig in te vullen voordat het dossier vertrekt.",
    next: "Na indiening voert het systeem automatisch een AI pre-screening uit op volledigheid en veelvoorkomende issues.",
  },
  {
    id: "prescreening",
    label: "AI pre-screening",
    actor: "ai",
    summary: "Automatische controle",
    detail:
      "Direct na indiening controleert het systeem het dossier automatisch op volledigheid en veelvoorkomende issues. De AI blokkeert nooit — ze signaleert enkel aandachtspunten en stelt een reviewer-briefing op.",
    next: "Het secretariaat controleert vervolgens of het dossier klaar is om aan het bureau voor te leggen.",
  },
  {
    id: "klaar-check",
    label: "Toewijzing reviewer",
    actor: "human",
    summary: "Toewijzing & AI-briefing",
    detail:
      "Het secretariaat bevestigt de volledigheid van het dossier, verwerkt de AI-signalen en wijst een reviewer toe. AI levert daarbij een samenvatting met bronverwijzingen.",
    next: "Hierna weegt de reviewer de AI-signalen bewust af en formuleert feedback of voorwaarden.",
  },
  {
    id: "bureau",
    label: "Menselijke review",
    actor: "human",
    summary: "Inhoudelijke beoordeling",
    detail:
      "De reviewer weegt AI-signalen bewust, valideert, verwerpt of vult aan en formuleert feedback of voorwaarden. Het menselijk oordeel blijft leidend.",
    next: "Na de review bespreekt en beslist het bureau, en de voorzitter ondertekent het advies.",
  },
  {
    id: "advies",
    label: "Beslissing & advies",
    actor: "human",
    summary: "Beslissing bezorgd",
    detail:
      "Het bureau bespreekt en beslist, houdt de audit trail zichtbaar en de voorzitter ondertekent het advies. Communicatie blijft menselijk en herleidbaar.",
    next: "Zodra het advies is verwerkt, wordt het dossier afgerond of start een slimme revisieronde.",
  },
  {
    id: "afgerond",
    label: "Revisieronde",
    actor: "system",
    summary: "Slimme revisieronde",
    detail:
      "Bij een hersubmissie vergelijkt AI de aanpassingen met de gestelde voorwaarden, ter ondersteuning van de menselijke check. Eenmaal aanvaard is het dossier afgerond en gearchiveerd.",
    next: "Het dossier is volledig afgehandeld.",
  },
];

const ACTOR_LABEL: Record<StageActor, string> = {
  human: "Mens",
  ai: "AI",
  system: "Systeem",
};

export function stageForId(id: ProcessStageId): ProcessStage {
  return PROCESS_STAGES.find((stage) => stage.id === id) ?? PROCESS_STAGES[0]!;
}

interface ProcessStepperProps {
  /** De huidige fase van het dossier — bepaalt done/active/upcoming. */
  currentStageId?: ProcessStageId;
  /** Toon het actor-label (Mens / AI / Systeem) onder elke fase. */
  showActor?: boolean;
}

export function ProcessStepper({ currentStageId, showActor = true }: ProcessStepperProps) {
  const currentIndex = currentStageId
    ? PROCESS_STAGES.findIndex((stage) => stage.id === currentStageId)
    : -1;

  return (
    <ol className="process-stepper">
      {PROCESS_STAGES.map((stage, index) => {
        const isDone = currentIndex >= 0 && index < currentIndex;
        const isActive = index === currentIndex;
        const stepClass = isActive
          ? "process-stepper__step process-stepper__step--active"
          : isDone
            ? "process-stepper__step process-stepper__step--done"
            : "process-stepper__step";

        return (
          <li key={stage.id} className={stepClass}>
            <span className="process-stepper__node">{isDone ? "✓" : index + 1}</span>
            <span className="process-stepper__label">{stage.label}</span>
            {showActor && (
              <span className="process-stepper__actor">{ACTOR_LABEL[stage.actor]}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

interface ProcessNextHintProps {
  currentStageId: ProcessStageId;
}

/** Eenregelige "wat gebeurt hierna" hint onder de compacte stepper. */
export function ProcessNextHint({ currentStageId }: ProcessNextHintProps) {
  const stage = stageForId(currentStageId);
  return (
    <div className="process-next">
      <span className="process-next__label">Wat volgt?</span>
      <span className="process-next__text">{stage.next}</span>
    </div>
  );
}
